import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import { useEvents } from '../../contexts/EventsContext';
import { useAuth } from '../../contexts/AuthContext';
import { AttentionNotification } from '../../hooks/useAttentionNotifications';
import { ActivityRecord } from '../Dashboard/dashboardUtils';
import { t } from '../../utils/translations';
import {
  formatEventDateRange,
  formatEventTimeRange,
  getEventDisplayStyles,
} from '../Calendar/eventUtils';
import {
  BadgePermissionState,
  getBadgePermissionState,
  requestBadgePermission,
  syncAppBadge,
} from '../../utils/pwa';
import { PushSyncStatus, syncFcmToken } from '../../utils/pushNotifications';

const ANIMATION_MS = 180;
const SHEET_ANIMATION_MS = 280;

interface NotificationsPanelProps {
  open: boolean;
  onClose: () => void;
  items: AttentionNotification[];
  /**
   * Sources still settling. "Nu ai notificări noi" is a claim, not a placeholder — showing it
   * before the data lands states something false, so the list waits instead.
   */
  loading?: boolean;
  onDismiss: (id: string) => Promise<void>;
  onOpenAssignment: (activity: ActivityRecord) => void;
  onDismissAssignment: (activityId: string) => Promise<void>;
}

const accentForItem = (
  item: AttentionNotification,
  eventAccent: string
): string => {
  if (item.type === 'assignment_outcome') {
    return item.payload.activity?.type === 'case_proposal_declined'
      ? '#DC2626'
      : '#059669';
  }
  switch (item.type) {
    case 'event':
      return eventAccent;
    case 'assignment':
      return '#C99700';
    case 'appointment':
      return '#2563EB';
    case 'stale_report':
      return '#D97706';
    case 'frequency_overdue':
      return '#EA580C';
    case 'consent_uploaded':
      return '#0D9488';
    case 'monthly_report':
      return '#7C3AED';
    case 'session_report_submitted':
      return '#7C3AED';
    case 'monthly_report_submitted':
      return '#6D28D9';
    default:
      return '#94A3B8';
  }
};

const typeLabel = (item: AttentionNotification): string => {
  switch (item.type) {
    case 'event':
      return t.notifications.typeEvent;
    case 'assignment':
      return t.notifications.typeAssignment;
    case 'assignment_outcome':
      return item.payload.activity?.type === 'case_proposal_declined'
        ? t.notifications.typeProposalDeclined
        : t.notifications.typeProposalOutcome;
    case 'appointment':
      return t.notifications.typeAppointment;
    case 'stale_report':
      return t.notifications.typeStaleReport;
    case 'frequency_overdue':
      return t.notifications.typeFrequencyOverdue;
    case 'consent_uploaded':
      return t.notifications.typeConsentUploaded;
    case 'monthly_report':
      return t.notifications.typeMonthlyReport;
    case 'session_report_submitted':
      return t.notifications.typeSessionReportSubmitted;
    case 'monthly_report_submitted':
      return t.notifications.typeMonthlyReportSubmitted;
    default:
      return '';
  }
};

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  open,
  onClose,
  items,
  loading = false,
  onDismiss,
  onOpenAssignment,
  onDismissAssignment,
}) => {
  const { currentUser } = useAuth();
  const { markEventsAsRead } = useEvents();
  const navigate = useNavigate();
  const eventStyles = getEventDisplayStyles();
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);
  const [badgePermission, setBadgePermission] = useState<BadgePermissionState>('unsupported');
  const [pushStatus, setPushStatus] = useState<PushSyncStatus | null>(null);

  useEffect(() => {
    if (!open) return;
    setBadgePermission(getBadgePermissionState());
    // Re-checked on every open: permission can be granted while registration still fails, and
    // on iOS there is no console to read the reason from.
    if (currentUser?.id) {
      void syncFcmToken(currentUser.id).then(setPushStatus);
    }
  }, [open, currentUser?.id]);

  const handleEnableBadge = async () => {
    const result = await requestBadgePermission();
    setBadgePermission(result);
    if (result === 'granted') {
      syncAppBadge(items.length);
      if (currentUser?.id) {
        // Cleared before the sync starts: badgePermission flips to granted immediately, so a
        // stale 'permission-not-granted' from the panel-open check would surface as a failure
        // banner for as long as registration takes.
        setPushStatus(null);
        setPushStatus(await syncFcmToken(currentUser.id));
      }
    }
  };

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setVisible(true));
      });
      return () => window.cancelAnimationFrame(id);
    }

    setVisible(false);
    const timeout = window.setTimeout(() => setMounted(false), SHEET_ANIMATION_MS);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const media = window.matchMedia('(max-width: 767px)');
    if (!media.matches) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const markEventRead = async (eventId: string) => {
    await markEventsAsRead([eventId]);
  };

  const handleRegistrationClick = async (
    e: React.MouseEvent,
    item: AttentionNotification
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const event = item.payload.event;
    if (!event?.registrationUrl) return;
    await markEventRead(event.id);
    window.open(event.registrationUrl, '_blank', 'noopener,noreferrer');
  };

  const handleItemClick = async (item: AttentionNotification) => {
    if (item.type === 'event' && item.payload.event) {
      const event = item.payload.event;
      onClose();
      const d = event.startDate;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      navigate(`/calendar?date=${y}-${m}-${day}`);
      void markEventRead(event.id);
      return;
    }

    if (item.type === 'assignment' && item.payload.activity) {
      const activity = item.payload.activity;
      onClose();

      // Proposals: open Accept/Refuse dialog; do not dismiss until action.
      if (activity.type === 'case_proposed') {
        onOpenAssignment(activity);
        return;
      }

      // Force-assign: go to the case, then clear the bell item.
      const caseId = activity.metadata?.caseId
        ? String(activity.metadata.caseId)
        : '';
      if (caseId) {
        navigate(`/cases?caseId=${caseId}`);
      }
      void onDismissAssignment(activity.id);
      return;
    }

    if (item.type === 'assignment_outcome' && item.payload.activity) {
      const activity = item.payload.activity;
      onClose();
      const caseId = activity.metadata?.caseId
        ? String(activity.metadata.caseId)
        : '';
      if (activity.type === 'case_proposal_declined') {
        if (caseId) {
          navigate(
            `/admin?tab=cases&caseId=${encodeURIComponent(caseId)}&reassign=1`
          );
        } else {
          navigate('/admin?tab=cases');
        }
        return;
      }
      navigate('/admin?tab=cases');
      void onDismissAssignment(activity.id);
      return;
    }

    if (item.type === 'appointment' && item.payload.appointment) {
      const apt = item.payload.appointment;
      onClose();
      const d = new Date(apt.date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      navigate(`/calendar?date=${y}-${m}-${day}`);
      void onDismiss(item.id);
      return;
    }

    if (item.type === 'stale_report' && item.payload.caseItem) {
      const caseId = item.payload.caseItem.id;
      onClose();
      navigate(`/cases?caseId=${caseId}`);
      void onDismiss(item.id);
      return;
    }

    if (item.type === 'frequency_overdue' && item.payload.caseItem) {
      const caseId = item.payload.caseItem.id;
      onClose();
      navigate(`/cases?caseId=${caseId}`);
      void onDismiss(item.id);
      return;
    }

    if (item.type === 'monthly_report') {
      // Open the form only — keep the bell item until the report is actually submitted.
      onClose();
      navigate('/monthly-report');
      return;
    }

    if (item.type === 'session_report_submitted') {
      const caseId =
        item.payload.caseId ||
        (item.payload.activity?.metadata?.caseId
          ? String(item.payload.activity.metadata.caseId)
          : '');
      onClose();
      if (caseId) {
        navigate(`/admin?tab=cases&caseId=${encodeURIComponent(caseId)}&reports=1`);
      } else {
        navigate('/admin?tab=cases');
      }
      void onDismiss(item.id);
      return;
    }

    if (item.type === 'monthly_report_submitted') {
      onClose();
      navigate('/admin?tab=reports');
      void onDismiss(item.id);
      return;
    }

    if (item.type === 'consent_uploaded') {
      onClose();
      navigate('/admin?tab=consents');
      void onDismiss(item.id);
    }
  };

  if (!mounted) return null;

  const renderTitle = () => (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <BellIcon className="h-5 w-5 shrink-0 text-brand-600" />
        <h2 className="text-sm font-semibold text-slate-900">{t.notifications.title}</h2>
      </div>
      {items.length > 0 && (
        <p className="mt-0.5 pl-7 text-xs text-slate-500">{t.notifications.subtitle}</p>
      )}
    </div>
  );

  const renderPermissionBanner = () =>
    badgePermission === 'default' ? (
      <div className="flex items-start gap-2 border-b border-slate-100 bg-brand-50 px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-900">{t.pwa.badgeTitle}</p>
          <p className="mt-0.5 text-[11px] leading-relaxed text-slate-600">{t.pwa.badgeHint}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            void handleEnableBadge();
          }}
          className="shrink-0 rounded-full bg-brand-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-brand-700"
        >
          {t.pwa.badgeButton}
        </button>
      </div>
    ) : badgePermission !== 'granted' ? (
      <p className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-[11px] leading-relaxed text-slate-600">
        {badgePermission === 'denied'
          ? t.pwa.badgeBlocked
          : badgePermission === 'needs-install'
            ? t.pwa.badgeNeedsInstall
            : t.pwa.badgeUnsupported}
      </p>
    ) : pushStatus && pushStatus !== 'registered' ? (
      // Permission is granted but the device still is not registered. The code stays visible:
      // on a phone there is no console, and it is the only way to tell these cases apart.
      <p className="border-b border-slate-100 bg-amber-50 px-4 py-2.5 text-[11px] leading-relaxed text-amber-900">
        {t.pwa.badgeRegisterFailed} <span className="text-amber-700/70">({pushStatus})</span>
      </p>
    ) : null;

  const renderList = () => (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain md:max-h-[min(28rem,70vh)]">
      {loading ? (
        // Shaped like the real rows, so the list fills in rather than jumping.
        <ul className="divide-y divide-slate-100" aria-hidden="true">
          {[0, 1, 2].map((row) => (
            <li key={row} className="px-4 py-3">
              <span className="block h-3 w-20 rounded bg-slate-100 motion-safe:animate-pulse" />
              <span className="mt-2 block h-3.5 w-40 rounded bg-slate-100 motion-safe:animate-pulse" />
              <span className="mt-1.5 block h-3 w-52 rounded bg-slate-100 motion-safe:animate-pulse" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500">{t.notifications.empty}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="flex w-full flex-col items-start px-4 py-3 text-left transition-colors hover:bg-slate-50 active:bg-slate-100"
                style={{
                  borderLeft: `3px solid ${accentForItem(item, eventStyles.accent)}`,
                }}
                onClick={() => {
                  void handleItemClick(item);
                }}
              >
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                  {typeLabel(item)}
                </span>
                <span className="mt-1.5 text-sm font-semibold text-slate-900">{item.title}</span>
                <span className="mt-0.5 text-xs leading-relaxed text-slate-500">{item.detail}</span>

                {item.type === 'event' && item.payload.event && (
                  <div className="mt-2 w-full space-y-1.5">
                    <p className="text-xs text-slate-500">
                      {formatEventDateRange(item.payload.event)} ·{' '}
                      {formatEventTimeRange(item.payload.event)}
                    </p>
                    {item.payload.event.description && (
                      <p className="line-clamp-2 whitespace-pre-wrap break-words text-xs text-slate-500">
                        {item.payload.event.description}
                      </p>
                    )}
                    {item.payload.event.registrationUrl && (
                      <>
                        <p className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                          {t.events.registrationMandatory}
                        </p>
                        <a
                          href={item.payload.event.registrationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-xs font-medium text-brand-700 hover:underline"
                          onClick={(e) => {
                            void handleRegistrationClick(e, item);
                          }}
                        >
                          {t.events.registrationLink}
                        </a>
                      </>
                    )}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const desktopPanel = (
    <div
      className={[
        'absolute right-0 top-full z-50 mt-1.5 hidden w-[min(22rem,calc(100vw-1rem))] origin-top-right flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg transition-[opacity,transform] ease-out md:flex',
        visible
          ? 'translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none -translate-y-1 scale-95 opacity-0',
      ].join(' ')}
      style={{ transitionDuration: `${ANIMATION_MS}ms` }}
      role="dialog"
      aria-modal="false"
    >
      <div className="border-b border-slate-100 px-4 py-3">{renderTitle()}</div>
      {renderPermissionBanner()}
      {renderList()}
    </div>
  );

  const mobileSheet =
    typeof document === 'undefined'
      ? null
      : createPortal(
          <div className="md:hidden" data-notifications-sheet>
            <div
              className={[
                'fixed inset-0 z-[60] bg-slate-900/40 transition-opacity ease-out',
                visible ? 'opacity-100' : 'pointer-events-none opacity-0',
              ].join(' ')}
              style={{ transitionDuration: `${SHEET_ANIMATION_MS}ms` }}
              aria-hidden="true"
              onClick={onClose}
            />
            <div
              className={[
                'fixed inset-0 z-[70] flex flex-col bg-white transition-transform ease-out',
                visible ? 'translate-y-0' : 'translate-y-full',
              ].join(' ')}
              style={{
                transitionDuration: `${SHEET_ANIMATION_MS}ms`,
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="notifications-sheet-title"
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div id="notifications-sheet-title">{renderTitle()}</div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
                >
                  {t.common.done}
                </button>
              </div>
              {renderPermissionBanner()}
              {renderList()}
            </div>
          </div>,
          document.body
        );

  return (
    <>
      {desktopPanel}
      {mobileSheet}
    </>
  );
};

export default NotificationsPanel;
